import { useEffect, useState } from "react";
import { Radar, Users, UserCog, Layers, TrendingUp, FileSignature, Receipt, Percent, ShoppingCart, X, ExternalLink } from "lucide-react";
import api from "../../lib/api";
import { eur, pct } from "../../lib/format";
import { useViewRole } from "../../context/ViewRoleContext";
import { ScopeSwitcher } from "../common/ScopeSwitcher";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";

const TYPE_VENTE = [
  { v: "all", l: "Toutes ventes" },
  { v: "Projet", l: "Projets" },
  { v: "Vente en direct", l: "Direct" },
];
const METRIC_LABEL = {
  ca_potentiel: "CA potentiel", ca_signe: "CA signé", ca_facture: "CA facturé",
  marge_signee: "Marge signée", nb_dossiers: "Dossiers", nb_signes: "Signés",
};
const STATUT_COLOR = { COMMANDE: "text-emerald-400", LIVRE: "text-emerald-400", PERDU: "text-red-400" };

export default function StatsAvancees() {
  const { canSeeMargins, isAdmin } = useViewRole();
  const [scope, setScope] = useState("real");
  const [typeVente, setTypeVente] = useState("all");
  const [data, setData] = useState(null);
  const [drill, setDrill] = useState(null); // {metric, dimension, valeur, label}
  const [drillData, setDrillData] = useState(null);

  useEffect(() => {
    api.get(`/analytics/stats?scope=${scope}&type_vente=${encodeURIComponent(typeVente)}`).then((r) => setData(r.data)).catch(() => {});
  }, [scope, typeVente]);

  const openDrill = (metric, dimension, valeur, label) => {
    if (!canSeeMargins && metric === "marge_signee") return;
    setDrill({ metric, dimension, valeur, label });
    setDrillData(null);
    api.get(`/analytics/dossiers?scope=${scope}&type_vente=${encodeURIComponent(typeVente)}&metric=${metric}&dimension=${dimension}&valeur=${encodeURIComponent(valeur || "")}`)
      .then((r) => setDrillData(r.data)).catch(() => setDrillData(null));
  };

  if (!data) return <div className="text-slate-500">Chargement des statistiques…</div>;
  const t = data.totaux;

  return (
    <div className="space-y-6" data-testid="stats-view">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Radar className="h-6 w-6 text-amber-400" />
            <h1 className="font-head text-3xl font-extrabold tracking-tight">Statistiques avancées</h1>
          </div>
          <p className="text-slate-400 text-sm mt-1">Analyses traçables jusqu'aux dossiers sources · aucun chiffre simulé.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {isAdmin && <ScopeSwitcher scope={scope} setScope={setScope} />}
          <div className="flex gap-1 p-1 rounded-lg bg-[#111827] border border-[#1E293B]">
            {TYPE_VENTE.map((o) => (
              <button key={o.v} data-testid={`stats-tv-${o.v}`} onClick={() => setTypeVente(o.v)}
                className={`px-3 py-1.5 rounded-md text-xs font-semibold transition-colors ${typeVente === o.v ? "bg-amber-500 text-[#0B0F17]" : "text-slate-400 hover:text-slate-200"}`}>
                {o.l}
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* Totaux (cliquables) */}
      <div className="grid grid-cols-2 lg:grid-cols-4 xl:grid-cols-7 gap-3">
        <TotalCard testId="stat-total-dossiers" icon={Layers} color="#94A3B8" title="Dossiers" value={t.nb_dossiers} onClick={() => openDrill("nb_dossiers", "", "", "Tous les dossiers")} />
        <TotalCard testId="stat-total-potentiel" icon={TrendingUp} color="#38BDF8" title="CA potentiel" value={eur(t.ca_potentiel)} onClick={() => openDrill("ca_potentiel", "", "", "CA potentiel — global")} />
        <TotalCard testId="stat-total-signe" icon={FileSignature} color="#10B981" title="CA signé" value={eur(t.ca_signe)} onClick={() => openDrill("ca_signe", "", "", "CA signé — global")} />
        <TotalCard testId="stat-total-facture" icon={Receipt} color="#F59E0B" title="CA facturé" value={eur(t.ca_facture)} onClick={() => openDrill("ca_facture", "", "", "CA facturé — global")} />
        <TotalCard testId="stat-total-marge" icon={Percent} color="#34D399" title="Marge signée" value={canSeeMargins ? eur(t.marge_signee) : "•••"} onClick={() => openDrill("marge_signee", "", "", "Marge signée — global")} />
        <TotalCard testId="stat-total-transfo" icon={Percent} color="#A78BFA" title="Transfo." value={pct(t.taux_transformation_pct)} sub={`${t.nb_signes}/${t.nb_dossiers}`} />
        <TotalCard testId="stat-total-panier" icon={ShoppingCart} color="#22D3EE" title="Panier moyen" value={eur(t.panier_moyen)} sub="par signé" />
      </div>

      <DimTable title="Par source d'acquisition" icon={Radar} rows={data.par_source} dimension="source" canSeeMargins={canSeeMargins} onDrill={openDrill} testId="stats-source" />
      <DimTable title="Par type de client" icon={Users} rows={data.par_type_client} dimension="type_client" canSeeMargins={canSeeMargins} onDrill={openDrill} testId="stats-type-client" />
      <DimTable title="Par commercial" icon={UserCog} rows={data.par_commercial} dimension="commercial" canSeeMargins={canSeeMargins} onDrill={openDrill} testId="stats-commercial" />
      <DimTable title="Par famille de produits" icon={Layers} rows={data.par_famille} dimension="famille" canSeeMargins={canSeeMargins} onDrill={openDrill} testId="stats-famille" />

      {/* Drill-down */}
      <Sheet open={!!drill} onOpenChange={(o) => !o && setDrill(null)}>
        <SheetContent side="right" className="bg-[#0B0F17] border-[#1E293B] text-slate-100 w-full sm:max-w-2xl overflow-y-auto" data-testid="drill-sheet">
          <SheetHeader>
            <SheetTitle className="font-head text-lg text-slate-100 flex items-center gap-2">
              <ExternalLink className="h-4 w-4 text-amber-400" /> {drill?.label}
            </SheetTitle>
          </SheetHeader>
          {!drillData ? (
            <div className="mt-6 text-slate-500 text-sm">Chargement des dossiers…</div>
          ) : (
            <div className="mt-4 space-y-4">
              <div className="rounded-lg bg-[#111827] border border-amber-500/20 p-3 flex items-center justify-between">
                <span className="text-sm text-slate-400">{METRIC_LABEL[drillData.metric]} · {drillData.count} dossier(s)</span>
                <span className="font-mono-metric text-lg font-bold text-amber-400" data-testid="drill-total">
                  {drillData.is_amount ? eur(drillData.total) : drillData.total}
                </span>
              </div>
              <div className="space-y-2">
                {drillData.dossiers.map((d) => (
                  <div key={d.id} className="rounded-lg bg-[#111827] border border-[#1E293B] p-3" data-testid={`drill-row-${d.id}`}>
                    <div className="flex items-center justify-between gap-2">
                      <div className="min-w-0">
                        <div className="font-medium truncate">{d.client_nom}</div>
                        <div className="text-xs text-slate-500 font-mono-metric truncate">{d.reference_interne || d.numero} · {d.famille || "—"} · {d.type_vente}</div>
                      </div>
                      <span className="font-mono-metric text-sm font-semibold shrink-0">
                        {drillData.metric === "marge_signee"
                          ? (canSeeMargins ? eur(d.marge) : "•••")
                          : eur(d.montant)}
                      </span>
                    </div>
                    <div className="mt-1.5 flex flex-wrap items-center gap-2 text-[11px]">
                      <span className={`font-semibold ${STATUT_COLOR[d.statut] || "text-slate-400"}`}>{d.statut}</span>
                      <span className="text-slate-600">·</span>
                      <span className="text-slate-500">{d.type_client || "—"}</span>
                      {drillData.metric === "marge_signee" && canSeeMargins && (
                        <span className={`px-1.5 py-0.5 rounded border text-[10px] ${d.marge_source === "historique" ? "bg-sky-500/10 text-sky-400 border-sky-500/20" : "bg-slate-500/10 text-slate-300 border-slate-500/20"}`}>
                          marge {d.marge_source}
                        </span>
                      )}
                    </div>
                  </div>
                ))}
                {drillData.dossiers.length === 0 && <p className="text-sm text-slate-500">Aucun dossier.</p>}
              </div>
            </div>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}

function TotalCard({ icon: Icon, color, title, value, sub, onClick, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      disabled={!onClick}
      className={`text-left rounded-xl bg-[#111827] border border-[#1E293B] p-4 transition-colors ${onClick ? "hover:border-amber-500/40 cursor-pointer" : "cursor-default"}`}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] uppercase tracking-wider text-slate-500 font-medium">{title}</span>
        <Icon className="h-3.5 w-3.5" style={{ color }} />
      </div>
      <div className="mt-2 font-mono-metric text-lg font-bold tracking-tight">{value}</div>
      {sub && <div className="text-[10px] text-slate-500">{sub}</div>}
    </button>
  );
}

function Num({ value, onClick, className = "", testId }) {
  return (
    <td className={`px-3 py-2.5 text-right font-mono-metric ${className}`}>
      {onClick ? (
        <button data-testid={testId} onClick={onClick} className="hover:text-amber-400 hover:underline decoration-dotted underline-offset-2 transition-colors">
          {value}
        </button>
      ) : value}
    </td>
  );
}

function DimTable({ title, icon: Icon, rows, dimension, canSeeMargins, onDrill, testId }) {
  return (
    <div className="rounded-xl bg-[#111827] border border-[#1E293B] overflow-hidden" data-testid={testId}>
      <div className="flex items-center gap-2 px-5 py-3.5 border-b border-[#1E293B]">
        <Icon className="h-4 w-4 text-amber-400" />
        <h2 className="font-head text-sm font-bold">{title}</h2>
        <span className="text-[11px] text-slate-600 ml-auto">clic sur un chiffre → dossiers sources</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-[#1E293B]">
              <th className="px-5 py-2.5 font-medium">{title.replace("Par ", "")}</th>
              <th className="px-3 py-2.5 font-medium text-right">Dossiers</th>
              <th className="px-3 py-2.5 font-medium text-right">CA potentiel</th>
              <th className="px-3 py-2.5 font-medium text-right">CA signé</th>
              <th className="px-3 py-2.5 font-medium text-right">CA facturé</th>
              {canSeeMargins && <th className="px-3 py-2.5 font-medium text-right">Marge</th>}
              <th className="px-3 py-2.5 font-medium text-right">Transfo.</th>
              <th className="px-3 py-2.5 font-medium text-right">Panier moy.</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.cle} className="border-b border-[#1E293B]/60 hover:bg-[#0B0F17]/40" data-testid={`row-${dimension}-${r.cle}`}>
                <td className="px-5 py-2.5 font-medium text-slate-200">{r.cle}</td>
                <Num value={r.nb_dossiers} className="text-slate-300" onClick={() => onDrill("nb_dossiers", dimension, r.cle, `${title} — ${r.cle} · Dossiers`)} testId={`drill-${dimension}-${r.cle}-nb`} />
                <Num value={eur(r.ca_potentiel)} className="text-sky-400" onClick={() => onDrill("ca_potentiel", dimension, r.cle, `${title} — ${r.cle} · CA potentiel`)} />
                <Num value={eur(r.ca_signe)} className="text-emerald-400" onClick={() => onDrill("ca_signe", dimension, r.cle, `${title} — ${r.cle} · CA signé`)} testId={`drill-${dimension}-${r.cle}-signe`} />
                <Num value={eur(r.ca_facture)} className="text-amber-400" onClick={() => onDrill("ca_facture", dimension, r.cle, `${title} — ${r.cle} · CA facturé`)} />
                {canSeeMargins && <Num value={eur(r.marge_signee)} className="text-emerald-300" onClick={() => onDrill("marge_signee", dimension, r.cle, `${title} — ${r.cle} · Marge signée`)} />}
                <td className="px-3 py-2.5 text-right font-mono-metric text-slate-300">{pct(r.taux_transformation_pct)}</td>
                <td className="px-3 py-2.5 text-right font-mono-metric text-slate-300">{eur(r.panier_moyen)}</td>
              </tr>
            ))}
            {rows.length === 0 && <tr><td colSpan={8} className="px-5 py-4 text-sm text-slate-500">Aucune donnée.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
